import {
  Box,
  Button,
  Dialog,
  DialogContent,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Tab,
  Tabs,
  TextField,
} from "@mui/material";
import PropTypes from "prop-types";
import EventFormFields from "./EventFormFields";
import TaskFormFields from "./TaskFormFields";
import ReminderFormFields from "./ReminderFormFields";

const EventFormDialog = ({
  open,
  onClose,
  dialogTab,
  dialogTitle,
  onTabChange,
  onTitleChange,
  onSave,
  onDelete,
  loading = false,
  eventFormProps,
  taskFormProps,
  reminderFormProps,
  isEditing = false,
  updateScope = 1,
  onUpdateScopeChange,
}) => {
  const handleTabChange = (event, value) => {
    // When editing an existing item, keep the type fixed
    if (isEditing) return;
    onTabChange(event, value);
  };
  const handleSave = () => {
    if (!dialogTitle.trim()) {
      return; // Title validation
    }
    if (onSave) {
      onSave();
    }
  };

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={onClose}>
      <DialogContent sx={{ p: 3 }}>
        <TextField
          fullWidth
          label="Add title"
          placeholder="Add title"
          value={dialogTitle}
          onChange={(e) => onTitleChange(e.target.value)}
          variant="standard"
          InputLabelProps={{ shrink: true }}
          sx={{ mb: 2 }}
          required
          error={!dialogTitle.trim()}
          helperText={!dialogTitle.trim() ? "Title is required" : ""}
        />
        <Tabs
          value={dialogTab}
          onChange={handleTabChange}
          textColor="primary"
          indicatorColor="primary"
          sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}
        >
          <Tab
            value="event"
            label="Event"
            disabled={isEditing && dialogTab !== "event"}
          />
          <Tab
            value="task"
            label="Task"
            disabled={isEditing && dialogTab !== "task"}
          />
          <Tab
            value="reminder"
            label="Reminders"
            disabled={isEditing && dialogTab !== "reminder"}
          />
        </Tabs>
        {dialogTab === "event" && (
          <EventFormFields {...eventFormProps} isEditing={isEditing} />
        )}
        {dialogTab === "task" && <TaskFormFields {...taskFormProps} />}
        {dialogTab === "reminder" && (
          <ReminderFormFields {...reminderFormProps} />
        )}

        {/* Update scope options (only shown when editing) */}
        {isEditing && onUpdateScopeChange && (
          <FormControl component="fieldset" sx={{ width: "100%", mt: 3 }}>
            <FormLabel
              component="legend"
              sx={{
                fontSize: "14px",
                fontWeight: "600",
                color: "#222",
                mb: 1,
              }}
            >
              Update scope:
            </FormLabel>
            <RadioGroup
              row
              value={updateScope}
              onChange={(e) => onUpdateScopeChange(Number(e.target.value))}
            >
              <FormControlLabel
                value={1}
                control={<Radio size="small" />}
                label="This event only"
                sx={{
                  "& .MuiFormControlLabel-label": {
                    fontSize: "13px",
                    color: "#333",
                  },
                }}
              />
              <FormControlLabel
                value={2}
                control={<Radio size="small" />}
                label="This and following"
                sx={{
                  "& .MuiFormControlLabel-label": {
                    fontSize: "13px",
                    color: "#333",
                  },
                }}
              />
              <FormControlLabel
                value={3}
                control={<Radio size="small" />}
                label="All events"
                sx={{
                  "& .MuiFormControlLabel-label": {
                    fontSize: "13px",
                    color: "#333",
                  },
                }}
              />
            </RadioGroup>
          </FormControl>
        )}

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: 1,
          }}
        >
          {/* Delete button (only shown when editing) */}
          {isEditing && onDelete && (
            <Button
              variant="text"
              color="error"
              onClick={onDelete}
              disabled={loading}
              sx={{ mr: "auto" }}
            >
              Delete
            </Button>
          )}
          {/* Action buttons (Cancel & Save) */}
          <Box sx={{ ml: "auto" }}>
            <Button
              variant="text"
              sx={{ mr: 1 }}
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={loading || !dialogTitle.trim()}
            >
              {loading ? "Saving..." : "Save"}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

EventFormDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  dialogTab: PropTypes.string.isRequired,
  dialogTitle: PropTypes.string.isRequired,
  onTabChange: PropTypes.func.isRequired,
  onTitleChange: PropTypes.func.isRequired,
  onSave: PropTypes.func,
  onDelete: PropTypes.func,
  loading: PropTypes.bool,
  eventFormProps: PropTypes.object.isRequired,
  taskFormProps: PropTypes.object.isRequired,
  reminderFormProps: PropTypes.object.isRequired,
  isEditing: PropTypes.bool,
  updateScope: PropTypes.number,
  onUpdateScopeChange: PropTypes.func,
};

export default EventFormDialog;
